import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../utils/supabase';
import { getSessionWithRetry } from '../utils/authSessionUtils';
import {
  resolveGoogleProfile,
  createPlayerRecordFromProfile,
} from '../services/playerOnboardingService';
import Button from '../components/common/Button';
import { Input } from '../components/common/Form';

/**
 * CompleteProfile — standalone route rendered at /auth/complete-profile.
 *
 * Shown when a session exists but no players row does, e.g. new Google users
 * who haven't chosen a competition category yet, or legacy accounts with a
 * missing player row.
 *
 * Guards:
 *   - No session  → redirect /  (global gate will show login)
 *   - Player row already exists → redirect /
 *
 * On submit: createPlayerRecordFromProfile() inserts the players row, then
 * persists the profile back into auth user_metadata so future metadata
 * lookups work. The global onAuthStateChange listener will then hydrate
 * currentUser normally on /.
 */
const CompleteProfile = ({ onClearAuthState }) => {
  const navigate = useNavigate();

  const [authInitializing, setAuthInitializing] = useState(true);
  const [authUser, setAuthUser] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const [formData, setFormData] = useState({
    first_name: '',
    last_name: '',
    gender: '',
  });
  const [validationErrors, setValidationErrors] = useState({});

  useEffect(() => {
    let cancelled = false;

    const init = async () => {
      const { session } = await getSessionWithRetry(6, 120);

      if (cancelled) return;

      if (!session) {
        navigate('/', { replace: true });
        return;
      }

      // If a player row already exists, the user shouldn't be here.
      const { data: rows } = await supabase
        .from('players')
        .select('id')
        .eq('auth_id', session.user.id)
        .limit(1);

      if (cancelled) return;

      if (rows && rows.length > 0) {
        navigate('/', { replace: true });
        return;
      }

      // Pre-fill names from Google metadata when available.
      const resolved = resolveGoogleProfile(session.user);
      setFormData({
        first_name: resolved.first_name || '',
        last_name: resolved.last_name || '',
        gender: '',
      });
      setAuthUser(session.user);
      setAuthInitializing(false);
    };

    init();
    return () => { cancelled = true; };
  }, [navigate]);

  const validate = useCallback(() => {
    const errors = {};
    if (!formData.first_name.trim()) errors.first_name = 'First name is required.';
    if (!formData.last_name.trim()) errors.last_name = 'Last name is required.';
    if (!formData.gender) errors.gender = 'Competition category is required.';
    return errors;
  }, [formData]);

  const handleSignOut = async () => {
    setError(null);
    setLoading(true);
    try {
      onClearAuthState?.();
      const { error: signOutError } = await supabase.auth.signOut();
      if (signOutError) throw signOutError;
      navigate('/', { replace: true });
    } catch (err) {
      console.error('[AUTH] complete_profile_signout_error', err);
      setError('Could not sign out. Please try again.');
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);

    const errors = validate();
    if (Object.keys(errors).length > 0) {
      setValidationErrors(errors);
      return;
    }
    setValidationErrors({});

    setLoading(true);
    try {
      const result = await createPlayerRecordFromProfile(authUser, {
        first_name: formData.first_name.trim(),
        last_name: formData.last_name.trim(),
        gender: formData.gender,
      });

      if (result.duplicateEmail) {
        setError(
          'An account with this email already exists. If this is your account, ' +
          'please sign in with your email and password instead, or contact support.'
        );
        return;
      }

      // Persist metadata so future metadata-based lookups work.
      await supabase.auth.updateUser({
        data: {
          first_name: formData.first_name.trim(),
          last_name: formData.last_name.trim(),
          gender: formData.gender,
        },
      });

      // Navigate home — the global auth listener will hydrate currentUser.
      navigate('/', { replace: true });
    } catch (err) {
      console.error('[AUTH] profile_complete_error', err);
      setError('Failed to create your profile. Please try again or contact support.');
    } finally {
      setLoading(false);
    }
  };

  if (authInitializing) {
    return (
      <div className="flex min-h-screen flex-col items-center justify-center bg-background">
        <div className="flex flex-col items-center gap-3">
          <div className="h-8 w-8 animate-spin rounded-full border-4 border-primary border-t-transparent" />
          <p className="text-sm text-text-muted">Loading…</p>
        </div>
      </div>
    );
  }

  return (
    <div className="flex min-h-screen flex-col bg-background">
      <div className="flex flex-1 flex-col items-center justify-center p-4">
        <div className="w-full max-w-md rounded-lg border border-border bg-surface p-6 shadow-sm">
          <h2 className="mb-1 text-xl font-bold text-text-primary">Almost done</h2>
          <p className="mb-5 text-sm text-text-muted">
            Choose your competition category to complete your Shuttlr profile.
            {authUser?.email ? (
              <>
                {' '}
                Signed in as <span className="font-medium text-text-primary">{authUser.email}</span>.
              </>
            ) : null}
          </p>

          {error && (
            <div className="mb-4 rounded border border-red-400 bg-red-100 px-4 py-3 text-sm text-red-700">
              {error}
            </div>
          )}

          <form onSubmit={handleSubmit} noValidate>
            <div className="mb-4">
              <label className="mb-1 block text-sm font-medium text-text-primary">
                First name <span className="text-red-500">*</span>
              </label>
              <Input
                type="text"
                value={formData.first_name}
                onChange={(e) =>
                  setFormData((f) => ({ ...f, first_name: e.target.value }))
                }
                placeholder="First name"
                error={validationErrors.first_name}
              />
              {validationErrors.first_name && (
                <p className="mt-1 text-xs text-red-600">{validationErrors.first_name}</p>
              )}
            </div>

            <div className="mb-4">
              <label className="mb-1 block text-sm font-medium text-text-primary">
                Last name <span className="text-red-500">*</span>
              </label>
              <Input
                type="text"
                value={formData.last_name}
                onChange={(e) =>
                  setFormData((f) => ({ ...f, last_name: e.target.value }))
                }
                placeholder="Last name"
                error={validationErrors.last_name}
              />
              {validationErrors.last_name && (
                <p className="mt-1 text-xs text-red-600">{validationErrors.last_name}</p>
              )}
            </div>

            <div className="mb-6">
              <label className="mb-1 block text-sm font-medium text-text-primary">
                Competition category <span className="text-red-500">*</span>
              </label>
              <p className="mb-2 text-xs text-text-muted">
                Used for matchmaking, draws, and Elo ratings.
              </p>
              <div className="flex gap-3">
                {['male', 'female'].map((val) => (
                  <button
                    key={val}
                    type="button"
                    onClick={() =>
                      setFormData((f) => ({ ...f, gender: val }))
                    }
                    className={[
                      'flex-1 rounded-md border px-4 py-2 text-sm font-medium transition-colors',
                      formData.gender === val
                        ? 'border-primary bg-primary text-white'
                        : 'border-border bg-background text-text-primary hover:border-primary',
                    ].join(' ')}
                  >
                    {val === 'male' ? "Men's" : "Women's"}
                  </button>
                ))}
              </div>
              {validationErrors.gender && (
                <p className="mt-1 text-xs text-red-600">{validationErrors.gender}</p>
              )}
            </div>

            <Button
              type="submit"
              variant="primary"
              disabled={loading}
              className="w-full"
            >
              {loading ? 'Creating profile…' : 'Complete setup'}
            </Button>

            <div className="mt-4 text-center">
              <Button
                type="button"
                variant="link"
                disabled={loading}
                onClick={handleSignOut}
                className="text-sm"
              >
                Sign out and use a different account
              </Button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default CompleteProfile;
